/**
 * lbs-cart-drawer.js — v2
 *
 * Fixes applied vs v1:
 * - Removed renderShip() call (was undefined → crashed render on load)
 * - Fixed updateBadge() to update inner <span> not destroy Trade theme badge structure
 * - Removed publish(cartUpdate) from updateBadge() to prevent render loop
 * - Single cartUpdate subscriber (no duplicate)
 * - Added ATC button click intercept (capture phase) for mobile Trade theme web component
 * - Broadened form selector to include /cart/add?section_id= variants
 */
(function () {
  'use strict';

  /* ─── Selectors ─── */
  const $ = (id) => document.getElementById(id);

  /* ─── Focus trap ─── */
  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  function visibleFocusable(el) {
    return Array.from(el.querySelectorAll(FOCUSABLE)).filter(
      (n) => !!(n.offsetWidth || n.offsetHeight || n.getClientRects().length)
    );
  }

  function onTab(e) {
    if (e.key !== 'Tab') return;
    const d = $('lbs-cart-drawer');
    if (!d || !d.classList.contains('is-open')) return;
    const nodes = visibleFocusable(d);
    if (!nodes.length) return;
    const first = nodes[0], last = nodes[nodes.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first || !d.contains(document.activeElement)) {
        e.preventDefault(); last.focus();
      }
    } else {
      if (document.activeElement === last || !d.contains(document.activeElement)) {
        e.preventDefault(); first.focus();
      }
    }
  }

  function onFocusin(e) {
    const d = $('lbs-cart-drawer');
    if (!d || !d.classList.contains('is-open')) return;
    if (!d.contains(e.target)) {
      e.stopImmediatePropagation();
      const nodes = visibleFocusable(d);
      if (nodes.length) nodes[0].focus();
    }
  }

  /* ─── Scroll lock ─── */
  let _scrollY = 0;
  let _prevBodyPosition = '';
  let _prevBodyTop = '';
  let _prevBodyWidth = '';

  function lockScroll() {
    _scrollY = window.scrollY;
    _prevBodyPosition = document.body.style.position;
    _prevBodyTop = document.body.style.top;
    _prevBodyWidth = document.body.style.width;
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + _scrollY + 'px';
    document.body.style.width = '100%';
  }

  function unlockScroll() {
    document.body.style.position = _prevBodyPosition;
    document.body.style.top = _prevBodyTop;
    document.body.style.width = _prevBodyWidth;
    window.scrollTo(0, _scrollY);
  }

  /* ─── Open / Close ─── */
  let _prevFocus = null;

  function openDrawer() {
    const d = $('lbs-cart-drawer');
    const o = $('lbs-cart-overlay');
    if (!d) return;
    _prevFocus = document.activeElement;
    lockScroll();
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    if (o) o.classList.add('is-open');
    document.addEventListener('keydown', onTab);
    document.addEventListener('focusin', onFocusin, true);
    document.querySelectorAll('[data-lbs-cart-trigger]').forEach((el) =>
      el.setAttribute('aria-expanded', 'true')
    );
    const closeBtn = $('lbs-cd-close');
    const target = closeBtn || visibleFocusable(d)[0];
    if (target) setTimeout(() => target.focus(), 60);
  }

  function closeDrawer() {
    const d = $('lbs-cart-drawer');
    const o = $('lbs-cart-overlay');
    if (!d) return;
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    if (o) o.classList.remove('is-open');
    unlockScroll();
    document.removeEventListener('keydown', onTab);
    document.removeEventListener('focusin', onFocusin, true);
    document.querySelectorAll('[data-lbs-cart-trigger]').forEach((el) =>
      el.setAttribute('aria-expanded', 'false')
    );
    if (_prevFocus && document.contains(_prevFocus) && typeof _prevFocus.focus === 'function') {
      setTimeout(() => _prevFocus.focus(), 60);
    }
    _prevFocus = null;
  }

  window.lbsCartDrawer = { open: openDrawer, close: closeDrawer };

  /* ─── Loader ─── */
  function showLoader() {
    const l = $('lbs-cd-loader');
    if (l) l.classList.add('is-active');
  }

  function hideLoader() {
    const l = $('lbs-cd-loader');
    if (l) l.classList.remove('is-active');
  }

  /* ─── Toast ─── */
  function toast(msg, type) {
    const existing = document.querySelector('.lbs-cd__toast');
    if (existing) existing.remove();
    const d = $('lbs-cart-drawer');
    if (!d) return;
    const t = document.createElement('div');
    t.className = 'lbs-cd__toast lbs-cd__toast--' + (type || 'error');
    t.setAttribute('role', 'alert');
    t.setAttribute('aria-live', 'polite');
    t.textContent = msg;
    d.appendChild(t);
    setTimeout(() => {
      t.style.opacity = '0';
      setTimeout(() => t.remove(), 320);
    }, 4000);
  }

  /* ─── Money ─── */
  function money(cents) {
    const n = Math.max(0, cents);
    if (window.Shopify && window.Shopify.formatMoney) return window.Shopify.formatMoney(n);
    return 'RM ' + (n / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* ─── Escape HTML ─── */
  function esc(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  }

  /* ─── Image URL ─── */
  function imgUrl(raw, w) {
    if (!raw) return '';
    try {
      const u = new URL(raw.startsWith('//') ? 'https:' + raw : raw);
      u.searchParams.set('width', String(w));
      return u.toString();
    } catch (_) {
      return raw + (raw.includes('?') ? '&' : '?') + 'width=' + w;
    }
  }

  /* ─── Fetch with timeout ─── */
  async function fetchWithTimeout(url, options, ms) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), ms || 10000);
    try {
      const res = await fetch(url, Object.assign({}, options, { signal: ctrl.signal }));
      clearTimeout(timer);
      return res;
    } catch (e) {
      clearTimeout(timer);
      throw e;
    }
  }

  /* ─── Fetch cart ─── */
  async function fetchCart() {
    const res = await fetchWithTimeout('/cart.js', {
      headers: { 'Content-Type': 'application/json' }
    }, 10000);
    if (!res.ok) throw new Error('Cart fetch failed ' + res.status);
    return res.json();
  }

  /* ─── Render: cart items ─── */
  function renderCart(cart) {
    const body   = $('lbs-cd-body');
    const footer = $('lbs-cd-footer');
    const count  = $('lbs-cd-count');
    const sub    = $('lbs-cd-subtotal');
    if (!body) return;

    // Count badge
    if (count) {
      count.innerHTML = cart.item_count
        + ' <span class="lbs-cd__badge-label">'
        + (cart.item_count === 1 ? 'item' : 'items') + '</span>';
    }

    // Subtotal
    if (sub) sub.textContent = money(cart.total_price);

    // Footer
    if (footer) footer.style.display = cart.item_count === 0 ? 'none' : '';

    // Empty state
    if (cart.item_count === 0) {
      body.innerHTML = [
        '<div class="lbs-cd__empty" id="lbs-cd-empty">',
        '<div class="lbs-cd__empty-icon">',
        '<svg width="52" height="52" viewBox="0 0 52 52" fill="none">',
        '<circle cx="26" cy="26" r="25" stroke="currentColor" stroke-width="1.2" stroke-dasharray="4 3" opacity="0.25"/>',
        '<path d="M14 16h4l2.5 13h13l2.5-9H18" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>',
        '<circle cx="22" cy="33" r="1.5" fill="currentColor"/>',
        '<circle cx="31" cy="33" r="1.5" fill="currentColor"/>',
        '</svg></div>',
        '<p class="lbs-cd__empty-title">Your cart is empty</p>',
        '<p class="lbs-cd__empty-sub">Add something to get started.</p>',
        '<button class="lbs-cd__continue" id="lbs-cd-continue" type="button">Continue Shopping</button>',
        '</div>'
      ].join('');
      const c = $('lbs-cd-continue');
      if (c) c.addEventListener('click', closeDrawer);
      return;
    }

    // Items
    let h = '<div class="lbs-cd__items" id="lbs-cd-items">';
    cart.items.forEach(function (item) {
      const disc = item.original_line_price !== item.final_line_price;
      const varHtml = (item.variant_title && item.variant_title !== 'Default Title')
        ? '<span class="lbs-cd__item-variant">' + esc(item.variant_title) + '</span>' : '';
      const wasHtml = disc
        ? '<s class="lbs-cd__price-was">' + money(item.original_line_price) + '</s>' : '';
      const priceClass = 'lbs-cd__price-final' + (disc ? ' is-sale' : '');
      const src = (item.featured_image && item.featured_image.url)
        ? imgUrl(item.featured_image.url, 160) : '';
      const imgHtml = src
        ? '<img src="' + src + '" alt="' + esc((item.featured_image && item.featured_image.alt) || item.title) + '" width="72" height="72" loading="lazy">'
        : '';
      const minusQty = Math.max(0, item.quantity - 1);

      h += '<div class="lbs-cd__item" data-key="' + esc(item.key) + '">';
      h += '<a class="lbs-cd__item-img" href="' + esc(item.url) + '">' + imgHtml + '</a>';
      h += '<div class="lbs-cd__item-info">';
      h += '<span class="lbs-cd__item-vendor">' + esc(item.vendor) + '</span>';
      h += '<a class="lbs-cd__item-title" href="' + esc(item.url) + '" title="' + esc(item.product_title) + '">' + esc(item.product_title) + '</a>';
      h += varHtml;
      h += '<div class="lbs-cd__item-row">';
      h += '<div class="lbs-cd__qty">';
      h += '<button class="lbs-cd__qty-btn lbs-cd-minus" data-key="' + esc(item.key) + '" data-qty="' + minusQty + '" aria-label="Decrease" type="button">&#8722;</button>';
      h += '<span class="lbs-cd__qty-val">' + item.quantity + '</span>';
      h += '<button class="lbs-cd__qty-btn lbs-cd-plus" data-key="' + esc(item.key) + '" data-qty="' + (item.quantity + 1) + '" aria-label="Increase" type="button">+</button>';
      h += '</div>';
      h += '<div class="lbs-cd__item-price">' + wasHtml + '<span class="' + priceClass + '">' + money(item.final_line_price) + '</span></div>';
      h += '</div></div>';
      h += '<button class="lbs-cd__item-remove" data-key="' + esc(item.key) + '" data-qty="0" aria-label="Remove ' + esc(item.title) + '" type="button">';
      h += '<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M9 3L3 9M3 3l6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
      h += '</button></div>';
    });
    h += '</div>';
    body.innerHTML = h;
  }

  /* ─── Header badge ─── */
  function updateBadge(count) {
    ['.cart-count-bubble', '#lbs-header-cart-count', '.lbs4-cart-count', '[data-cart-count]'].forEach((sel) => {
      document.querySelectorAll(sel).forEach((el) => {
        if (count > 0) {
          // Update inner span only — preserves Trade theme badge structure
          const inner = el.querySelector('span[aria-hidden]') || el.querySelector('span') || el;
          inner.textContent = count;
          el.style.display = '';
          el.removeAttribute('hidden');
        } else {
          el.style.display = 'none';
        }
      });
    });
    // Do NOT publish cartUpdate here — causes render → updateBadge → publish → render loop
  }

  /* ─── Full render ─── */
  // renderShip removed — no free shipping bar in drawer
  function render(cart) {
    renderCart(cart);
    updateBadge(cart.item_count);
  }

  /* ─────────────────────────────────────
     QTY QUEUE
     - Sequential /cart/change.js (variant-safe)
     - Remove bypasses debounce immediately
     - Optimistic UI with rollback
  ───────────────────────────────────── */
  let qtyLock  = false;
  const qtyQ   = {};
  let qtyTimer = null;
  const snap   = {};

  function queueQty(key, qty, itemEl) {
    if (!(key in snap)) {
      const v = itemEl ? itemEl.querySelector('.lbs-cd__qty-val') : null;
      snap[key] = v ? parseInt(v.textContent, 10) : null;
    }

    if (qty === 0) {
      delete qtyQ[key];
      clearTimeout(qtyTimer);
      qtyQ[key] = 0;
      flushQ();
      return;
    }

    // Optimistic UI
    if (itemEl) {
      const v = itemEl.querySelector('.lbs-cd__qty-val');
      if (v) v.textContent = qty;
      const minus = itemEl.querySelector('.lbs-cd-minus');
      const plus  = itemEl.querySelector('.lbs-cd-plus');
      if (minus) minus.dataset.qty = String(Math.max(0, qty - 1));
      if (plus)  plus.dataset.qty  = String(qty + 1);
    }

    qtyQ[key] = qty;
    clearTimeout(qtyTimer);
    qtyTimer = setTimeout(flushQ, 300);
  }

  function rollback() {
    fetchCart().then(render).catch(() => {});
    Object.keys(snap).forEach((k) => delete snap[k]);
  }

  async function flushQ() {
    if (qtyLock) {
      clearTimeout(qtyTimer);
      qtyTimer = setTimeout(flushQ, 150);
      return;
    }
    const entries = Object.entries(qtyQ);
    if (!entries.length) return;

    const batch = {};
    entries.forEach(([k, v]) => { batch[k] = v; delete qtyQ[k]; });

    qtyLock = true;
    showLoader();
    try {
      for (const [key, qty] of Object.entries(batch)) {
        const res = await fetchWithTimeout('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: qty })
        }, 10000);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || 'Update failed (' + res.status + ')');
        }
      }
      const cart = await fetchCart();
      render(cart);
      Object.keys(snap).forEach((k) => delete snap[k]);
    } catch (e) {
      console.error('[LBS Cart] Qty failed:', e);
      toast(e.message || 'Could not update quantity.', 'error');
      rollback();
    } finally {
      qtyLock = false;
      hideLoader();
      if (Object.keys(qtyQ).length) qtyTimer = setTimeout(flushQ, 0);
    }
  }

  /* ─────────────────────────────────────
     ADD TO CART
  ───────────────────────────────────── */
  let atcLock = false;

  async function addToCart(formData, btn) {
    if (atcLock) return;
    atcLock = true;
    if (btn) btn.disabled = true;
    showLoader();
    try {
      const params = new URLSearchParams();
      formData.forEach((v, k) => params.append(k, v));
      const res = await fetchWithTimeout('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString()
      }, 10000);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add to cart');
      }
      const cart = await fetchCart();
      render(cart);
      openDrawer();
    } catch (e) {
      console.error('[LBS Cart] ATC failed:', e);
      toast(e.message || 'Something went wrong. Please try again.', 'error');
    } finally {
      atcLock = false;
      if (btn) btn.disabled = false;
      hideLoader();
    }
  }

  /* ─────────────────────────────────────
     ORDER NOTE — debounced save
  ───────────────────────────────────── */
  let noteTimer = null;

  function saveNote(value) {
    clearTimeout(noteTimer);
    noteTimer = setTimeout(async () => {
      try {
        await fetchWithTimeout('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: value })
        }, 10000);
      } catch (e) {
        console.warn('[LBS Cart] Note save failed:', e);
      }
    }, 600);
  }

  /* ─────────────────────────────────────
     EVENTS
  ───────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', function () {

    // ── Close button ──
    const closeBtn = $('lbs-cd-close');
    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);

    // ── Overlay click → close ──
    const overlay = $('lbs-cart-overlay');
    if (overlay) overlay.addEventListener('click', closeDrawer);

    // ── Outside-click guard via pointerdown ──
    document.addEventListener('pointerdown', function (e) {
      const d = $('lbs-cart-drawer');
      if (!d || !d.classList.contains('is-open')) return;
      if (d.contains(e.target)) return;
      if (e.target.closest('[data-lbs-cart-trigger], [href="/cart"], .lbs4-cart-link, #lbs-header-cart')) return;
      closeDrawer();
    });

    // ── Continue shopping (initial empty render) ──
    const cont = $('lbs-cd-continue');
    if (cont) cont.addEventListener('click', closeDrawer);

    // ── Escape key ──
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeDrawer();
    });

    // ── Order note — debounced live save ──
    document.addEventListener('input', function (e) {
      if (e.target && e.target.id === 'lbs-cd-note') saveNote(e.target.value);
    });

    // ── ATC intercept — submit capture (desktop) ──
    document.addEventListener('submit', function (e) {
      const form = e.target.closest('form');
      if (!form || !form.action.includes('/cart/add')) return;
      e.preventDefault();
      e.stopPropagation();
      addToCart(new FormData(form), form.querySelector('[type="submit"],[name="add"]'));
    }, true);

    // ── ATC intercept — click capture (mobile Trade theme web component) ──
    document.addEventListener('click', function (e) {
      const btn = e.target.closest('button[name="add"].product-form__submit');
      if (!btn) return;
      const form = btn.closest('form');
      if (!form || !form.action.includes('/cart/add')) return;
      if (atcLock) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      addToCart(new FormData(form), btn);
    }, true);

    // ── Delegated: qty steppers / remove / cart icon ──
    document.addEventListener('click', function (e) {
      // Qty + remove buttons
      const qBtn = e.target.closest('.lbs-cd__qty-btn, .lbs-cd__item-remove');
      if (qBtn) {
        const key    = qBtn.dataset.key;
        const qty    = parseInt(qBtn.dataset.qty, 10);
        const itemEl = qBtn.closest('.lbs-cd__item');
        if (key && !isNaN(qty)) queueQty(key, qty, itemEl);
        return;
      }

      // Header cart icon → open drawer
      const cartLink = e.target.closest('[data-lbs-cart-trigger], [href="/cart"], .lbs4-cart-link, #lbs-header-cart');
      if (cartLink) {
        e.preventDefault();
        openDrawer();
      }
    });

    // ── Trade pubsub — sync render only, drawer open handled by addToCart() ──
    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
      subscribe(window.PUB_SUB_EVENTS.cartUpdate, function () {
        fetchCart().then(render).catch(function () {});
      });
    }

    // ── Suppress native Trade cart-drawer ──
    const native = document.querySelector('cart-drawer');
    if (native) native.style.display = 'none';

    // ── Hydrate drawer with real cart on page load ──
    fetchCart().then(render).catch(function (e) {
      console.warn('[LBS Cart] Hydration failed:', e);
    });

  });
})();